import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";
import { api, ApiError } from "../lib/api.js";
import { NxMark } from "../components/icons.js";
import { LoginScene } from "../components/LoginScene.js";

/**
 * Login em duas fases:
 *  - "password": senha comum (única fase se o usuário não tem 2FA).
 *  - "two-factor": abre no card se o backend responder `requires2fa`.
 *    Input de 6 dígitos + fallback "usar código de recuperação".
 */
type Phase =
  | { kind: "password" }
  | { kind: "two-factor"; challengeToken: string; backupMode: boolean };

export function LoginPage() {
  const { login, verifyLoginTwoFactor } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "password" });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Feature flag do "Esqueci minha senha" — se o backend não tem Resend
  // configurado, escondemos o link inteiramente. Uma única chamada no boot.
  const [recoveryEnabled, setRecoveryEnabled] = useState(false);
  // Banner de sucesso vindo do fluxo /reset-password (querystring resetOk=1).
  const resetOk = params.get("resetOk") === "1";
  const resetOkWith2fa = resetOk && params.get("twofa") === "1";

  useEffect(() => {
    api.passwordRecovery
      .status()
      .then((s) => setRecoveryEnabled(s.enabled))
      .catch(() => setRecoveryEnabled(false));
  }, []);

  const onPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const step = await login(email, password);
      if (step.kind === "requires_2fa") {
        setPhase({
          kind: "two-factor",
          challengeToken: step.challengeToken,
          backupMode: false,
        });
      } else {
        nav("/app", { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError("E-mail ou senha inválidos.");
        else if (err.status === 429)
          setError("Muitas tentativas. Tente novamente em alguns minutos.");
        else setError("Erro ao entrar. Tente novamente.");
      } else {
        setError("Erro de rede.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onTwoFactorSubmit = async (code: string) => {
    if (phase.kind !== "two-factor") return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyLoginTwoFactor(phase.challengeToken, code);
      nav("/app", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError(
            "Excesso de tentativas. Faça login novamente para receber um novo desafio.",
          );
          setPhase({ kind: "password" });
        } else if (err.status === 401) {
          if (err.code === "challenge_expired" || err.code === "invalid_challenge") {
            setError("Sessão de verificação expirou. Faça login de novo.");
            setPhase({ kind: "password" });
          } else {
            setError("Código incorreto.");
          }
        } else setError("Erro ao verificar código.");
      } else {
        setError("Erro de rede.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="nx-login">
      <div className="nx-login__stage">
        <div className="nx-login__hero">
          <span className="nx-login__hero-eyebrow">Painel de operação</span>
          <h2 className="nx-login__hero-title">
            Gestão de eletropostos <em>em tempo real</em>.
          </h2>
          <p className="nx-login__hero-lead">
            Monitore carregadores, sessões e telemetria elétrica dos seus pontos
            OCPP a partir de um único console. Feito para a operação, não para a
            vitrine.
          </p>
          {/* Capacidades do console, não leitura de instrumento: antes de
              autenticar não existe pátio, carregador nem demanda para
              reportar. Número aqui seria invenção com cara de medição. */}
          <ul className="nx-login__hero-caps">
            <li>Estado ao vivo dos carregadores conectados</li>
            <li>Sessões, energia e fases de carga por transação</li>
            <li>Alertas por carregador, subestação e horário de ponta</li>
          </ul>
          {/* Ilustração do pátio. Decorativa, pausável, e ausente do DOM
              abaixo do breakpoint do painel. Não recebe foco: o autoFocus
              continua sendo do campo de e-mail, no card ao lado. */}
          <LoginScene />
        </div>

        <div className="nx-login__card">
          <div className="nx-login__card-head">
            <div className="nx-wordmark nx-login__brand" aria-label="Nxvolt">
              <span className="nx-wordmark__mark">
                <NxMark size={30} />
              </span>
              <span className="nx-wordmark__text">
                <b>NX</b>
                <i>volt</i>
              </span>
            </div>
            <div className="nx-login__parent">uma solução Noxxon Sat</div>
          </div>

          {phase.kind === "password" ? (
            <>
              <h1 className="nx-login__title">Entrar no console</h1>
              <p className="nx-login__hint">Use o e-mail corporativo cadastrado.</p>

              {resetOk && (
                <div
                  role="status"
                  className="nx-success"
                  style={{ marginTop: "var(--nx-space-4)" }}
                >
                  ✓ Senha redefinida.{" "}
                  {resetOkWith2fa
                    ? "Faça login com sua nova senha e informe o código 2FA."
                    : "Faça login com sua nova senha."}
                </div>
              )}

              <form onSubmit={onPasswordSubmit} noValidate>
                <label className="nx-field">
                  <span>E-mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    placeholder="voce@empresa.com"
                    required
                    autoFocus
                  />
                </label>

                <label className="nx-field">
                  <span>Senha</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    required
                  />
                </label>

                {error && (
                  <div role="alert" className="nx-error">
                    {error}
                  </div>
                )}

                <div style={{ marginTop: "var(--nx-space-6)" }}>
                  <button
                    className="nx-btn nx-btn--full"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? "Entrando…" : "Entrar"}
                  </button>
                </div>
              </form>

              {recoveryEnabled && (
                <div
                  style={{
                    marginTop: "var(--nx-space-4)",
                    textAlign: "center",
                  }}
                >
                  <Link
                    to="/forgot-password"
                    className="nx-btn nx-btn--link"
                  >
                    Esqueci minha senha
                  </Link>
                </div>
              )}
            </>
          ) : (
            <TwoFactorStep
              backupMode={phase.backupMode}
              onSubmit={onTwoFactorSubmit}
              onBack={() => {
                setError(null);
                setPhase({ kind: "password" });
              }}
              onToggleMode={() =>
                setPhase({ ...phase, backupMode: !phase.backupMode })
              }
              submitting={submitting}
              error={error}
            />
          )}

          <div className="nx-login__footer">© {year} Noxxon Sat · Nxvolt</div>
        </div>
      </div>
    </div>
  );
}

interface StepProps {
  backupMode: boolean;
  onSubmit: (code: string) => void;
  onBack: () => void;
  onToggleMode: () => void;
  submitting: boolean;
  error: string | null;
}

function TwoFactorStep({
  backupMode,
  onSubmit,
  onBack,
  onToggleMode,
  submitting,
  error,
}: StepProps) {
  const [digits, setDigits] = useState<string[]>(Array.from({ length: 6 }, () => ""));
  const [backup, setBackup] = useState("");

  function updateDigit(i: number, v: string) {
    const clean = v.replace(/\D/g, "").slice(0, 1);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = clean;
      return next;
    });
    if (clean) {
      const nextEl = document.getElementById(`otp-${i + 1}`) as HTMLInputElement | null;
      nextEl?.focus();
      // Submissão automática no 6º dígito.
      if (i === 5) {
        const code = [...digits.slice(0, 5), clean].join("");
        if (code.length === 6) onSubmit(code);
      }
    }
  }

  function onDigitPaste(e: React.ClipboardEvent<HTMLInputElement>, i: number) {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste.length >= 2) {
      e.preventDefault();
      const arr = paste.split("").concat(Array(6).fill("")).slice(0, 6);
      setDigits(arr);
      if (paste.length === 6) onSubmit(paste);
      else document.getElementById(`otp-${Math.min(paste.length, 5)}`)?.focus();
    }
  }

  function onDigitKey(e: React.KeyboardEvent<HTMLInputElement>, i: number) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      const prev = document.getElementById(`otp-${i - 1}`) as HTMLInputElement | null;
      prev?.focus();
    }
  }

  const submitBackup = (e: FormEvent) => {
    e.preventDefault();
    if (backup.trim()) onSubmit(backup.trim());
  };

  return (
    <>
      <h1 className="nx-login__title">Verificação em dois passos</h1>
      <p className="nx-login__hint">
        {backupMode
          ? "Digite um código de recuperação (formato XXXX-XXXX)."
          : "Digite o código de 6 dígitos do seu app authenticator."}
      </p>

      {backupMode ? (
        <form onSubmit={submitBackup} noValidate>
          <label className="nx-field">
            <span>Código de recuperação</span>
            <input
              type="text"
              value={backup}
              onChange={(e) => setBackup(e.target.value.toUpperCase())}
              placeholder="ABCD-1234"
              autoFocus
              maxLength={9}
              className="mono"
            />
          </label>
          {error && (
            <div role="alert" className="nx-error">
              {error}
            </div>
          )}
          <button
            className="nx-btn nx-btn--full"
            type="submit"
            disabled={submitting || backup.trim().length < 8}
          >
            {submitting ? "Verificando…" : "Entrar"}
          </button>
        </form>
      ) : (
        <div>
          <div className="nx-otp" role="group" aria-label="Código de 6 dígitos">
            {digits.map((d, i) => (
              <input
                key={i}
                id={`otp-${i}`}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                onChange={(e) => updateDigit(i, e.target.value)}
                onPaste={(e) => onDigitPaste(e, i)}
                onKeyDown={(e) => onDigitKey(e, i)}
                autoFocus={i === 0}
                autoComplete="one-time-code"
                aria-label={`Dígito ${i + 1}`}
              />
            ))}
          </div>
          {error && (
            <div
              role="alert"
              className="nx-error"
              style={{ marginTop: "var(--nx-space-4)" }}
            >
              {error}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: "var(--nx-space-4)",
          display: "flex",
          justifyContent: "space-between",
          gap: "var(--nx-space-3)",
        }}
      >
        <button
          type="button"
          className="nx-btn nx-btn--link"
          onClick={onBack}
          disabled={submitting}
        >
          ← Voltar
        </button>
        <button
          type="button"
          className="nx-btn nx-btn--link"
          onClick={onToggleMode}
          disabled={submitting}
        >
          {backupMode ? "Usar app authenticator" : "Usar código de recuperação"}
        </button>
      </div>
    </>
  );
}
